/**
 * ChangePasswordRequiredPage — Écran de changement de mot de passe forcé.
 *
 * Affiche en plein écran après login si profiles.must_change_password === true.
 * Réutilise le layout du formulaire login (cohérence visuelle).
 *
 * Flux :
 *   1. supabase.auth.updateUser({ password })
 *   2. UPDATE profiles SET must_change_password = false, password_expires_at = null
 *   3. onSuccess() : SupabaseWrapper rafraichit l'état utilisateur
 */

import React, { useState } from 'react';
import { supabase } from '../SupabaseWrapper';
import { useViewport } from '../hooks/useViewport';

interface Props {
  authUserId: string;
  email: string;
  onSuccess: () => void;
  onLogout: () => void;
}

const MIN_PASSWORD_LENGTH = 8;

function ChangePasswordRequiredPage({ authUserId, email, onSuccess, onLogout }: Props) {
  const { isPhone } = useViewport();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      console.log('[ChangePasswordRequired] updateUser');
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) {
        setError(updateError.message || 'Impossible de mettre à jour le mot de passe.');
        return;
      }

      console.log('[ChangePasswordRequired] UPDATE profiles must_change_password=false');
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ must_change_password: false, password_expires_at: null })
        .eq('auth_user_id', authUserId);

      if (profileError) {
        console.error('[ChangePasswordRequired] Erreur UPDATE profiles:', profileError.message);
        setError('Mot de passe modifié mais profil non synchronisé. Reconnectez-vous.');
        return;
      }

      onSuccess();
    } catch (err) {
      console.error('[ChangePasswordRequired] Erreur:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du changement de mot de passe.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f1f5f9',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: isPhone ? 'calc(100vw - 24px)' : '460px',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        padding: isPhone ? '24px' : '40px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#fef3c7',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h1 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#1e293b',
            margin: '0 0 8px'
          }}>
            Changement de mot de passe requis
          </h1>
          <p style={{
            color: '#64748b',
            fontSize: '14px',
            margin: 0,
            lineHeight: '1.5'
          }}>
            Pour sécuriser votre compte, veuillez choisir un nouveau mot de passe avant d'accéder à l'application.
          </p>
        </div>

        <div style={{
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '10px 12px',
          marginBottom: '20px',
          fontSize: '13px',
          color: '#475569'
        }}>
          Compte&nbsp;: <strong style={{ color: '#1e293b' }}>{email}</strong>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              Nouveau mot de passe
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={`Au moins ${MIN_PASSWORD_LENGTH} caractères`}
              required
              autoFocus
              minLength={MIN_PASSWORD_LENGTH}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '15px',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.15s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              Confirmer le mot de passe
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Saisissez à nouveau le mot de passe"
              required
              minLength={MIN_PASSWORD_LENGTH}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '15px',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.15s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#64748b',
            cursor: 'pointer',
            userSelect: 'none'
          }}>
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Afficher les mots de passe
          </label>

          {error && (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '8px',
              padding: '10px 12px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#991b1b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#dc2626">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: loading ? '#93c5fd' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.15s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#2563eb'; }}
            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#3b82f6'; }}
          >
            {loading && (
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }}></div>
            )}
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            {loading ? 'Mise à jour...' : 'Mettre à jour mon mot de passe'}
          </button>
        </form>

        <div style={{
          textAlign: 'center',
          marginTop: '20px',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <button
            onClick={onLogout}
            type="button"
            style={{
              background: 'none',
              border: 'none',
              color: '#64748b',
              fontSize: '13px',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChangePasswordRequiredPage;
